import { useState } from 'react';
import { pendingResetFromHash, resetTokenUserId, stripPasswordResetFromHash } from '@turn-connect/shared';
import type { PendingPasswordReset } from '@turn-connect/shared';

/**
 * True when this page was opened from an emailed sign-in link: a "Forgot
 * password?" link (#password_reset_token=…) or any successful sign-in link
 * (#signed_in=1). Read when the module loads, before App clears the fragment,
 * so the sign-in check can't miss it. The link has just signed the user in
 * (a session cookie), and the app must look for that session even in a
 * browser that holds no saved user — e.g. after signing out. Without this the
 * app showed the public home page and, for a reset, dropped "Choose a new
 * password". A manager-required reset (password_reset_required=1) starts no
 * session, so it doesn't count.
 */
function signedInByLink(hash: string): boolean {
  const reset = pendingResetFromHash(hash);
  if (reset) return !reset.required;
  return new URLSearchParams(hash.replace(/^#/, '')).get('signed_in') === '1';
}

const openedFromSignInLink = typeof window !== 'undefined' && signedInByLink(window.location.hash);

export function arrivedFromSignInLink(): boolean {
  return openedFromSignInLink;
}

/** Removes the sign-in mark from a URL fragment, keeping anything else. */
function stripSignedIn(hash: string): string {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  if (!params.has('signed_in')) return hash;
  params.delete('signed_in');
  const rest = params.toString();
  return rest ? `#${rest}` : '';
}

/**
 * Reads the reset token the API puts in the URL fragment after an emailed
 * "Forgot password?" link, and clears it from the address bar and history
 * before anything else on the page can read it.
 */
export function takePasswordResetFromUrl(): PendingPasswordReset | null {
  const reset = pendingResetFromHash(window.location.hash);
  const { pathname, search, hash } = window.location;
  const cleaned = stripSignedIn(reset ? stripPasswordResetFromHash(hash) : hash);
  if (cleaned !== hash) {
    window.history.replaceState(window.history.state, '', pathname + search + cleaned);
  }
  return reset;
}

/** The pending "choose a new password" step: from the URL on load, or set after a code sign-in. */
export function usePendingPasswordReset() {
  return useState<PendingPasswordReset | null>(takePasswordResetFromUrl);
}

/**
 * Whether to offer "Choose a new password" to the signed-in user: only their
 * own token, never one meant for whoever used this browser before, and never
 * a manager-required reset (those users get no session until it is done, so
 * one seen alongside a session is already used).
 */
export function shouldOfferPasswordReset(reset: PendingPasswordReset | null, userId: string | undefined): reset is PendingPasswordReset {
  return !!reset && !reset.required && !!userId && resetTokenUserId(reset.token) === userId;
}
